import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import CheckIcon from "@mui/icons-material/CheckCircle";
import {
  formatDatetime,
  getCurrencySign,
  shortnString,
  catchError,
  formatOpponentAddress,
  validateLink,
} from "@_utils/helpers";
import WarningIcon from "@mui/icons-material/Warning";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import LangIcon from "@mui/icons-material/Language";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

import ActivateCasePopup from "@_components/popups/ActivateCasePopup/ActivateCasePopup";
import StripeInfoPopup from "@_components/popups/StripeInfoPopup/StripeInfoPopup";
import CancelCasePopup from "@_components/popups/CancelCasePopup/CancelCasePopup";
import ContactInfoPopup from "@_components/popups/ContactInfoPopup/ContactInfoPopup";
import SelectEmailFlowPopup from "@_components/popups/SelectEmailFlowPopup/SelectEmailFlowPopup";
import SelectTranslationPopup from "@_components/popups/SelectTranslationPopup/SelectTranslationPopup";
import ReminderPopup from "@_components/popups/ReminderPopup/ReminderPopup";
import MailPopup from "@_components/popups/MailPopup/MailPopup";
import { authService } from "@_services/auth.service";

import ClaimPopup from "@_components/popups/ClaimPopup/ClaimPopup";
import styles from "./OverviewSection.module.css";
import NextIcon from "@mui/icons-material/NavigateNextOutlined";
import FindingsPopup from "@_components/popups/FindingsPopup/FindingsPopup";
import AddFindingPopup from "@_components/popups/AddFindingPopup/AddFindingPopup";
import { languageActions } from "@_actions/language.actions";
import { emailSetupActions } from "@_actions/email_setup.actions";
import { caseActions } from "@_actions/case.actions";
import { casesActions } from "@_actions/cases.actions";
import { caseActivityActions } from "@_actions/caseActivity.actions";
import TemplatePopup from "@_components/popups/TemplatePopup/TemplatePopup";
import { AddCircleOutline, EmailOutlined } from "@mui/icons-material";
import { caseConstants } from "@_constants/case.constants";
import { emailConstants } from "@_constants/email_flows.constants";
import AreYouSurePopup from "@_components/popups/AreYouSurePopup/AreYouSurePopup";
import { Chip, ListSubheader } from "@mui/material";
import InfoPopup from "@_components/popups/InfoPopup/InfoPopup";
import AddCaseTagPopup from "@_components/popups/AddCaseTagPopup/AddCaseTagPopup";
import { customerActions } from "@_actions/customer.actions";
import { caseTemplateActions } from "@_actions/caseTemplate.actions";
import { templateActions } from "@_actions/template.actions";
import AppSnackbar from "@_components/AppSnackbar/AppSnackbar";
import { useSnackbar } from "@_hooks/useSnackbar";
import { useOverviewValidation } from "@_hooks/useOverviewValidation";
import OverviewHeader from "@_components/sections/OverviewHeader/OverviewHeader";

function OverviewSection(props) {
  const dispatch = useDispatch();
  const { data } = props;

  const emailSetupReducer = useSelector((state) => state.emailSetupReducer);
  const languageReducer = useSelector((state) => state.languageReducer);
  const caseReducer = useSelector((state) => state.caseReducer);
  const templateReducer = useSelector((state) => state.templateReducer);

  const [openActivateCasePopup, setOpenActivateCasePopup] = useState(false);
  const [openStripeInfoPopup, setOpenStripeInfoPopup] = useState(false);

  const [openCancelCasePopup, setOpenCancelCasePopup] = useState(false);
  const [templatePopupOpen, setTemplatePopupOpen] = useState(false);
  const [openResetCasePopup, setOpenResetCasePopup] = useState(false);
  const [openClaimPopup, setOpenClaimPopup] = useState(false);
  const [openContactInfoPopup, setOpenContactInfoPopup] = useState(false);
  const [openReminderPopup, setOpenReminderPopup] = useState(false);
  const [openAddCaseTagPopup, setOpenAddCaseTagPopup] = useState(false);
  const [reminderData, setReminderData] = useState(
    caseReducer.case[props.data.id],
  );

  const [openFindingsPopup, setOpenFindingsPopup] = useState(false);
  const [openAddFindingPopup, setOpenAddFindingPopup] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const { snackbarProps, showSnackbarError } = useSnackbar();

  const [openEmailFlowPopup, setOpenEmailFlowPopup] = useState(false);
  const [openTranslationPopup, setOpenTranslationPopup] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [emailData, setEmailData] = useState({});
  const [openMailPopup, setOpenMailPopup] = useState(false);
  // const [isTaxable, setIsTaxable] = useState(false);
  // const [validTax, setValidTax] = useState(false);
  const [isConnected, setIsConnected] = useState();
  const [stripeUrl, setStripeUrl] = useState();
  const [openPreviewMenu, setOpenPreviewMenu] = useState(false);
  const [openInfoPopup, setOpenInfoPopup] = useState(false);
  const [infoPopupText, setInfoPopupText] = useState("");
  const [InfoPopupTitle, setInfoPopupTitle] = useState("");
  const [openMenu, setOpenMenu] = useState(null);

  const selectedTax =
    {
      SALES_TAX: "Sales Tax",
      VAT: "VAT",
      GST: "GST",
    }[data.charge_details?.indirect_tax_type] || "";

  const emailSetup = emailSetupReducer.email_setup.active_email_setup;
  const displayedContactData =
    data.opponent_contact_details?.email && data.opponent_contact_details?.name
      ? data.opponent_contact_details.email +
        " - " +
        data.opponent_contact_details.name
      : data.opponent_contact_details?.email ||
        data.opponent_contact_details?.name ||
        "No contact details set";
  const flowId = data.email_flow?.id;
  const flow = flowId ? data.email_flow : undefined;
  const { validTranslation, validContact, validClaim, activable } =
    useOverviewValidation({ data, emailSetupReducer, languageReducer });

  /**
   * Loads email setup , case templates and available translations  when the component first mounts.
   *
   * This runs once to fetch the SMTP configuration and available templates
   * so they're ready for use in mail menus and template pickers.
   */
  useEffect(() => {
    dispatch(emailSetupActions.getEmailSetup());
    dispatch(caseTemplateActions.getCaseTemplates());
    dispatch(languageActions.getTranslations());
  }, [dispatch]);

  /**
  Sets the first finding (image) as the default preview when findings change.
   * @triggers
   * When findings change.
  */
  useEffect(() => {
    setSelectedImage(data.findings && data.findings[0]);
  }, [data.findings]);

  /**
   * Fetches email templates for the currently selected mail flow.
   *
   * If a flow is selected, load its templates. If not, clear the template list.
   * Adds a "New Email" option if the case is not in standby mode.
   * @triggers
   * When flow id changes.
   */
  useEffect(() => {
    if (flow?.id) {
      dispatch(templateActions.getTemplates(flow.id));
    } else {
      setTemplates([]);
    }
  }, [dispatch, flow?.id]);

  useEffect(() => {
    const templateGetStatus = templateReducer.status.TEMPLATE_GET;

    if (!flow?.id) {
      setTemplates([]);
      return;
    }

    if (templateGetStatus === "success") {
      const baseTemplates = templateReducer.flow_templates?.[flow.id] || [];
      const nextTemplates = [...baseTemplates];

      if (data.state !== caseConstants.CASE_STATE_STANDBY) {
        nextTemplates.push({
          id: emailConstants.NEW_MAIL,
          name: "New Email",
        });
      }

      setTemplates(nextTemplates);
      dispatch(templateActions.clearStatusMessage("TEMPLATE_GET"));
    } else if (catchError(templateGetStatus)) {
      showSnackbarError(templateGetStatus);
      dispatch(templateActions.clearStatusMessage("TEMPLATE_GET"));
      setTemplates([]);
    }
  }, [
    data.state,
    dispatch,
    flow?.id,
    showSnackbarError,
    templateReducer.flow_templates,
    templateReducer.status.TEMPLATE_GET,
  ]);

  const handleCloseMenu = (event) => {
    event.stopPropagation();
    setOpenMenu(null);
  };

  const handleOpenMenu = (event) => {
    event.stopPropagation();
    flow
      ? setOpenMenu(event.currentTarget)
      : handleOpenInfoPopup(
          "No mail flow assigned to the case.",
          "Please select a mail flow to send mails",
        );
  };

  const handleOpenPreviewMenu = (event) => {
    event.stopPropagation();
    flow
      ? setOpenPreviewMenu(event.currentTarget)
      : handleOpenInfoPopup(
          "No mail flow assigned to the case.",
          "Please select a mail flow to preview mails",
        );
  };

  const handleOpenInfoPopup = (title, text) => {
    setInfoPopupTitle(title);
    setInfoPopupText(text);
    setOpenInfoPopup(true);
  };

  const handleClosePreviewMenu = (event) => {
    event.stopPropagation();
    setOpenPreviewMenu(false);
  };

  const handleOpenEmailFlowPopup = (event) => {
    event.stopPropagation();
    setOpenEmailFlowPopup(true);
  };

  const handleOpenTemplatePopup = (event, template_id, name, type) => {
    event.stopPropagation();
    handleCloseMenu(event);
    handleClosePreviewMenu(event);
    if (template_id === emailConstants.NEW_MAIL) {
      setEmailData({
        content: "",
        name: emailConstants.NEW_MAIL,
        subject: "",
        opponentEmail: data.opponent_contact_details?.email,
      });
      setOpenMailPopup(true);
    } else {
      const to =
        type === emailConstants.TEMPLATE_TYPE_LETTER
          ? formatOpponentAddress(data.opponent_contact_details)
          : data.opponent_contact_details?.email;

      setEmailData({
        name,
        type,
        template_id,
        opponentEmail: to,
      });
      setOpenMailPopup(true);
    }
  };

  const langSelectionCLicked = () => {
    if (validTranslation) {
      setOpenTranslationPopup(true);
    } else {
      showSnackbarError(
        "You must create and activate a translation under Admin > Settlement Portal > Translations before activating this case.",
      );
    }
  };

  const openEvidancePopup = () => {
    if (data.findings.length > 0) setOpenFindingsPopup(data.findings);
    else setOpenAddFindingPopup(true);
  };

  const handleCaseActivation = () => {
    authService.getStripe().then(
      (results) => {
        if (results.status === "awaiting_onboarding") {
          setStripeUrl(results.onboarding_url);
          setIsConnected(false);
          setOpenStripeInfoPopup(true);
        } else {
          // either connected or diffrent status response with 200
          setIsConnected(true);
          setOpenActivateCasePopup(true);
        }
      },
      (error) => {
        showSnackbarError(error);
      },
    );
  };

  const handleAutoLogin = () => {
    if (!data.portal_url || !validateLink(data.portal_url)) {
      showSnackbarError("No valid portal link is available for this case.");
      return;
    }
    window.open(data.portal_url, "_blank", "noopener,noreferrer");
  };

  const handleResetCase = () => {
    data.was_activated
      ? dispatch(caseActions.updateCaseStateActivate(data.id))
      : dispatch(caseActions.updateCaseStateReset(data.id));
  };

  /**
   * Handles reset/activate case completion: refresh global lists, refetch case, close reset popup, or show errors.
   *
   * Used from reset/activate actions (`handleResetCase`, related popups). Uses a short timeout before
   * refetch so backend state can settle. Overlaps partially with the separate “not sent” activate effect.s
   *
   * @triggers
   * When reset/activate status changes or reset popup state relevant to error path.
   *
   * Issue 1: Uses setTimeout 100ms (Unreliable)
   * Issue 2: Status Clearing Not Immediate, Status stays "success" for 100ms, If user clicks reset again, effect runs again (race condition)
   * Issue 3: Error Path Only Clears if Popup is Open
   *
   * Improvements:
   * split into two effects, one for success path, one for error path?
   */
  useEffect(() => {
    if (
      caseReducer.status.CASE_UPDATE_STATE_RESET === "success" ||
      caseReducer.status.CASE_UPDATE_STATE_ACTIVATE === "success" ||
      caseReducer.status.CASE_UPDATE_STATE_ACTIVATE === "not sent"
    ) {
      dispatch(casesActions.getCases());
      dispatch(casesActions.getCasesCount());
      /* Activating/resetting a case creates new activities. Refetch them
      directly from here so the new cards show up */
      dispatch(caseActions.getCaseWithChargeDetails(data.id, data.overview));
      dispatch(caseActivityActions.getCaseActivities(data.id));
      setOpenActivateCasePopup(false);
      setOpenResetCasePopup(false);
      dispatch(caseActions.clearStateMessage("CASE_UPDATE_STATE_RESET"));
      dispatch(caseActions.clearStateMessage("CASE_UPDATE_STATE_ACTIVATE"));
      if (
        caseReducer.status.CASE_UPDATE_STATE_ACTIVATE === "not sent" &&
        !data.overview?.was_activated
      ) {
        handleOpenInfoPopup(
          "Initial email not sent",
          "The case is now activated, but the initial email could not be sent. The reason is shown in the failed email event in the case activity section. Try resending the email manually after having resolved the issue.",
        );
      }
    } else if (
      catchError(caseReducer.status.CASE_UPDATE_STATE_RESET) ||
      catchError(caseReducer.status.CASE_UPDATE_STATE_ACTIVATE)
    ) {
      showSnackbarError(
        catchError(caseReducer.status.CASE_UPDATE_STATE_RESET)
          ? caseReducer.status.CASE_UPDATE_STATE_RESET
          : caseReducer.status.CASE_UPDATE_STATE_ACTIVATE,
      );
      setOpenResetCasePopup(false);
      dispatch(caseActions.clearStateMessage("CASE_UPDATE_STATE_RESET"));
      dispatch(caseActions.clearStateMessage("CASE_UPDATE_STATE_ACTIVATE"));
    }
  }, [
    caseReducer.status.CASE_UPDATE_STATE_RESET,
    caseReducer.status.CASE_UPDATE_STATE_ACTIVATE,
    openResetCasePopup,
    dispatch,
    data.overview,
    data.id,
    showSnackbarError,
  ]);

  /**
   * Refreshes case and tag catalog after user deletes a tag from the case.
   * @triggers
   * When tag delete status changes.
   */
  useEffect(() => {
    if (caseReducer.status.CASE_DELETE_TAG === "success") {
      dispatch(caseActions.clearStateMessage("CASE_DELETE_TAG"));
      dispatch(caseActions.getCaseWithChargeDetails(data.id, data.overview));
      dispatch(customerActions.getCaseTags());
    } else if (catchError(caseReducer.status.CASE_DELETE_TAG)) {
      showSnackbarError(caseReducer.status.CASE_DELETE_TAG);
      dispatch(caseActions.clearStateMessage("CASE_DELETE_TAG"));
    }
  }, [
    caseReducer.status.CASE_DELETE_TAG,
    dispatch,
    data.id,
    data.overview,
    showSnackbarError,
  ]);

  const handleDeleteTag = (tagName) => {
    dispatch(caseActions.deleteCaseTag(data.id, tagName));
  };

  const renderTags = () => {
    var retval = [];
    if (data?.tags && Array.isArray(data.tags) && data.tags.length > 0) {
      for (const item of data.tags) {
        retval.push(
          <Chip
            className={styles.tag_chip}
            data-testid={`case-tag-${item}`}
            key={item}
            variant="outlined"
            title={item}
            label={item}
            onDelete={() => handleDeleteTag(item)}
          ></Chip>,
        );
      }
    }
    return retval;
  };

  const groupedTemplates = {
    triggeredInitial: templates.filter(
      (template) =>
        template.type === emailConstants.TEMPLATE_TYPE_TRIGGERED &&
        template.trigger_event === emailConstants.TRIGGER_EVENT_INITIAL,
    ),
    triggeredEvent: templates.filter(
      (template) =>
        template.type === emailConstants.TEMPLATE_TYPE_TRIGGERED &&
        template.trigger_event !== emailConstants.TRIGGER_EVENT_INITIAL,
    ),
    scheduled: templates.filter(
      (template) => template.type === emailConstants.TEMPLATE_TYPE_SCHEDULED,
    ),
    custom: templates.filter(
      (template) => template.type === emailConstants.TEMPLATE_TYPE_CUSTOM,
    ),
    letter: templates.filter(
      (template) => template.type === emailConstants.TEMPLATE_TYPE_LETTER,
    ),
  };

  const renderTemplateSection = (title, templateList) => {
    if (!templateList.length) return null;

    return (
      <>
        <ListSubheader>{title}</ListSubheader>
        {templateList.map((template, index) => (
          <MenuItem
            className={styles.template_dropdown}
            data-testid={`overview-template-menu-item-${template.id}`}
            key={index}
            onClick={(event) =>
              handleOpenTemplatePopup(
                event,
                template.id,
                template.name,
                template.type,
              )
            }
          >
            {template.name}
          </MenuItem>
        ))}
      </>
    );
  };

  return (
    <div>
      <div className={styles.root}>
        <OverviewHeader
          data={data}
          activable={activable}
          onAutoLogin={handleAutoLogin}
          onOpenCancelCasePopup={() => setOpenCancelCasePopup(true)}
          onCaseActivation={handleCaseActivation}
          onOpenResetCasePopup={() => setOpenResetCasePopup(true)}
        />
        <div className={styles.details_container}>
          <div className={styles.finding_section}>
            <div className={styles.image_list}>
              {data.findings && data.findings.length > 0 ? (
                <>
                  <div className={styles.no_finding_container_with_image}>
                    {(data.state === "STANDBY" ||
                      data.state === caseConstants.CASE_STATE_ACTIVE) && (
                      <div className={styles.btn_add_finding_with_image}>
                        <Button
                          className={styles.btn_add_finding}
                          onClick={() => setOpenAddFindingPopup(true)}
                        >
                          <b className={styles.plus_sign}>+</b>Add image use
                        </Button>
                      </div>
                    )}
                  </div>
                  <img
                    className={styles.image_item}
                    src={
                      selectedImage &&
                      selectedImage.original_image.original_image_url
                    }
                    alt="original url"
                    key={selectedImage && selectedImage.id}
                  />
                  <div className={styles.image_icons_container}>
                    {data.findings &&
                      data.findings.map((finding) => (
                        <img
                          className={
                            finding === selectedImage && selectedImage
                              ? styles.image_icon_selected
                              : styles.image_icon
                          }
                          src={finding.original_image.original_image_url}
                          alt="original url"
                          key={finding.id}
                          onMouseEnter={() => setSelectedImage(finding)}
                        />
                      ))}
                  </div>
                </>
              ) : (
                <div className={styles.no_finding_container}>
                  <div className={styles.add_finding_container}>
                    <Button
                      className={styles.btn_add_finding}
                      onClick={() => setOpenAddFindingPopup(true)}
                    >
                      <b className={styles.plus_sign}>+</b>Add image use
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <div
              className={styles.evidence_container}
              data-testid="overview-evidence-section"
              onClick={openEvidancePopup}
            >
              <div className={styles.evidence_text}>Image uses</div>
              <div className={styles.evidence_action}>
                <div className={styles.finding_count}>
                  {data.findings ? data.findings.length : 0} pic(s)
                </div>
                <NextIcon />
              </div>
            </div>
            <div className={styles.tag_section}>
              <div className={styles.tags_container}>{renderTags()}</div>
              <Tooltip title="Add Case Tag" placement="bottom" arrow>
                <AddCircleOutline
                  className={styles.add_tag_icon}
                  onClick={() => setOpenAddCaseTagPopup(true)}
                />
              </Tooltip>
            </div>
          </div>
          <div className={styles.overview_section}>
            {data.state === "STANDBY" && (
              <>
                <div className={styles.case_templage_container}>
                  <button
                    style={{ padding: "4px 4px 4px 4px", fontFamily: "Roboto" }}
                    onClick={() => setTemplatePopupOpen(true)}
                  >
                    AUTO FILL
                  </button>
                </div>
              </>
            )}

            <div
              className={`${styles.contact_container} `}
              data-testid="overview-language-section"
              onClick={langSelectionCLicked}
            >
              {data.state !== "STANDBY" ? (
                <></>
              ) : validTranslation ? (
                <div className={styles.valid_box}>
                  <CheckIcon />
                </div>
              ) : (
                <div className={styles.not_valid_box}>
                  <WarningIcon />
                </div>
              )}
              <div className={styles.title}>Portal Language</div>
              <div
                className={styles.template_container}
                href={"#"}
                rel="noopener noreferrer"
              >
                <div className={styles.template_icon}>
                  <LangIcon />
                </div>

                <div className={styles.template_title}>
                  {(data.translation &&
                    data.translation.language.toUpperCase()) ||
                    (languageReducer.translations &&
                      languageReducer.translations.default_translation_id &&
                      "DEFAULT") ||
                    "No active translation"}
                </div>
              </div>
            </div>

            <hr />

            <div
              className={`${styles.email_flow_container} ${
                data.state !== caseConstants.CASE_STATE_STANDBY &&
                data.state !== caseConstants.CASE_STATE_CANCELLED
                  ? styles.no_hover
                  : ""
              }`}
              data-testid="overview-email-flow-section"
              onClick={
                data.state === caseConstants.CASE_STATE_STANDBY ||
                data.state === caseConstants.CASE_STATE_CANCELLED
                  ? handleOpenEmailFlowPopup
                  : () => {}
              }
            >
              <div
                className={`${styles.email_flow_left_container} ${
                  data.state !== caseConstants.CASE_STATE_STANDBY &&
                  data.state !== caseConstants.CASE_STATE_CANCELLED
                    ? styles.disabled
                    : ""
                }`}
              >
                <div className={styles.title}>Mail Flow</div>
                <div
                  className={styles.template_container}
                  href={"#"}
                  rel="noopener noreferrer"
                >
                  <div className={styles.template_icon}>
                    <EmailOutlined />
                  </div>

                  <div className={styles.template_title}>
                    {(flow && flow.name) || "No mail flow is set"}
                  </div>
                </div>
              </div>

              {data.state !== "STANDBY" ? (
                <></>
              ) : flow ? (
                <div className={styles.valid_box}>
                  <CheckIcon />
                </div>
              ) : (
                <div className={styles.not_valid_box}>
                  <WarningIcon />
                </div>
              )}
              {(data.state === "ACTIVE" ||
                data.state === "VALID_LICENSE" ||
                data.state === "PAYMENT_PROCESSING" ||
                data.state === "PAID" ||
                data.state === "CANCELLED") && (
                <>
                  <Button
                    variant="outlined"
                    className={styles.btn_send_email}
                    data-testid="overview-send-mail-button"
                    onClick={handleOpenMenu}
                    endIcon={<ArrowDropDownIcon />}
                  >
                    Send Mail
                  </Button>
                  <Menu
                    id="simple-menu"
                    anchorEl={openMenu}
                    keepMounted={false}
                    open={Boolean(openMenu)}
                    onClose={handleCloseMenu}
                  >
                    {templates[templates.length - 1]?.id ===
                      emailConstants.NEW_MAIL && (
                      <MenuItem
                        className={styles.template_dropdown_first}
                        data-testid="overview-template-menu-item-new-mail"
                        onClick={(event) =>
                          handleOpenTemplatePopup(
                            event,
                            templates[templates.length - 1]?.id,
                            templates[templates.length - 1]?.name,
                          )
                        }
                      >
                        {templates[templates.length - 1]?.name}
                      </MenuItem>
                    )}

                    {renderTemplateSection(
                      `${emailConstants.TEMPLATE_TYPE_CUSTOM} EMAILS`,
                      groupedTemplates.custom,
                    )}
                    {renderTemplateSection("LETTERS", groupedTemplates.letter)}
                  </Menu>
                </>
              )}
              {data.state === caseConstants.CASE_STATE_STANDBY && (
                <>
                  <Button
                    variant="outlined"
                    className={styles.btn_send_email}
                    data-testid="overview-preview-mail-button"
                    onClick={handleOpenPreviewMenu}
                    endIcon={<ArrowDropDownIcon />}
                    style={{ width: "45%" }}
                  >
                    Preview mail
                  </Button>
                  <Menu
                    id="simple-menu"
                    anchorEl={openPreviewMenu}
                    keepMounted={false}
                    open={Boolean(openPreviewMenu)}
                    onClose={handleClosePreviewMenu}
                    onClick={(event) => event.stopPropagation()}
                  >
                    {templates?.length > 0 ? (
                      <>
                        {renderTemplateSection(
                          "INITIAL EMAIL",
                          groupedTemplates.triggeredInitial,
                        )}
                        {renderTemplateSection(
                          `${emailConstants.TEMPLATE_TYPE_SCHEDULED} EMAILS`,
                          groupedTemplates.scheduled,
                        )}
                        {renderTemplateSection(
                          "EVENT TRIGGERED EMAILS",
                          groupedTemplates.triggeredEvent,
                        )}
                        {renderTemplateSection(
                          `${emailConstants.TEMPLATE_TYPE_CUSTOM} EMAILS`,
                          groupedTemplates.custom,
                        )}
                        {renderTemplateSection(
                          "LETTERS",
                          groupedTemplates.letter,
                        )}
                      </>
                    ) : (
                      <MenuItem
                        onClick={(event) => event.stopPropagation()}
                        className={styles.template_dropdown}
                        data-testid="overview-template-menu-item-no-templates"
                      >
                        This mail flow contains no templates to be previewed.
                      </MenuItem>
                    )}
                  </Menu>
                </>
              )}
            </div>
            <hr />
            <div
              className={styles.contact_container}
              data-testid="overview-contact-section"
              onClick={() => setOpenContactInfoPopup(true)}
            >
              {data.state !== "STANDBY" ? (
                <></>
              ) : validContact ? (
                <div className={styles.valid_box}>
                  <CheckIcon />
                </div>
              ) : (
                <div className={styles.not_valid_box}>
                  <WarningIcon />
                </div>
              )}
              <div className={styles.title}>Website Owner Contact Info</div>

              <div className={styles.contact_info_box}>
                <div className={styles.contact_big_title}>
                  {displayedContactData}
                </div>
              </div>
            </div>
            <hr />
            <div
              className={styles.claim_container}
              data-testid="overview-claim-section"
              onClick={() => setOpenClaimPopup(true)}
            >
              {data.state !== "STANDBY" ? (
                <></>
              ) : validClaim ? (
                <div className={styles.valid_box}>
                  <CheckIcon />
                </div>
              ) : (
                <div className={styles.not_valid_box}>
                  <WarningIcon />
                </div>
              )}
              <div className={styles.title}>Licensing Offer</div>
              <div className={styles.claim_content}>
                <div>
                  <div>
                    <div className={styles.claim_info_box}>
                      <div className={styles.claim_small_title_component}>
                        Country
                      </div>
                      <div className={styles.claim_big_title}>
                        {data.state === "VALID_LICENSE"
                          ? "----"
                          : data.country || "----"}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className={styles.claim_info_box}>
                      <div className={styles.claim_small_title}>
                        Net License Fee
                      </div>
                      <div className={styles.claim_big_title}>
                        {data.state === "VALID_LICENSE" ||
                        (data.charge_details &&
                          !data.charge_details?.total_net_amount_findings)
                          ? "----"
                          : getCurrencySign(data.charge_details?.currency) +
                            " " +
                            data.charge_details?.total_net_amount_findings}
                      </div>
                    </div>
                    <div className={styles.claim_info_box}>
                      <div className={styles.claim_small_title}>
                        Gross Incl.{" "}
                        {data.charge_details &&
                          data.charge_details.indirect_tax_type !==
                            caseConstants.CASE_TAX_TYPE_NO_INDIRECT_TAX &&
                          data.charge_details.indirect_tax_type !==
                            caseConstants.CASE_TAX_TYPE_REVERSE_CHARGE &&
                          selectedTax + " & "}{" "}
                        Fees
                      </div>
                      <div className={styles.claim_big_title}>
                        {data.state === "VALID_LICENSE" ? (
                          "----"
                        ) : (
                          <span>
                            <span>
                              {data.charge_details &&
                              data.charge_details
                                .final_amount_gross_no_settlement
                                ? getCurrencySign(data.claim_details.currency) +
                                  " "
                                : ""}
                            </span>
                            <span>
                              {data.charge_details &&
                              data.charge_details
                                .final_amount_gross_no_settlement
                                ? data.charge_details
                                    .final_amount_gross_no_settlement
                                : "Not Set"}
                            </span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <div className={styles.claim_info_box}>
                    <div className={styles.claim_small_title}>Discount</div>
                    <div className={styles.claim_big_title}>
                      {data.state === "VALID_LICENSE"
                        ? "----"
                        : data.charge_details &&
                            data.charge_details.settlement_offer &&
                            Number(
                              data.charge_details.settlement_offer
                                .settlement_percentage,
                            ).toLocaleString(undefined, {
                              maximumFractionDigits: 2,
                            })
                          ? "% " +
                            Number(
                              data.charge_details.settlement_offer
                                .settlement_percentage,
                            ).toLocaleString(undefined, {
                              maximumFractionDigits: 2,
                            })
                          : "Not Set"}
                    </div>
                  </div>
                  <div className={styles.claim_info_box}>
                    <div className={styles.claim_small_title}>
                      Discount Deadline
                    </div>
                    <div className={styles.claim_big_title}>
                      {data.state === "VALID_LICENSE"
                        ? "----"
                        : data.charge_details &&
                            data.charge_details.settlement_offer
                          ? formatDatetime(
                              data.charge_details.settlement_offer
                                .settlement_date,
                              "short",
                            )
                          : "Not Set"}
                    </div>
                  </div>
                  <div className={styles.claim_info_box}>
                    <div className={styles.claim_small_title}>
                      Discounted Total
                    </div>
                    <div className={styles.claim_big_title}>
                      {data.state === "VALID_LICENSE" ? (
                        "----"
                      ) : (
                        <span>
                          <span>
                            {data.charge_details &&
                            data.charge_details.final_amount_gross_settlement
                              ? getCurrencySign(data.claim_details.currency) +
                                " "
                              : ""}
                          </span>
                          <span>
                            {data.charge_details &&
                            data.charge_details.final_amount_gross_settlement
                              ? data.charge_details
                                  .final_amount_gross_settlement
                              : "Not Set"}
                          </span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <hr />
            {data.state === "ACTIVE" && (
              <>
                <div className={styles.deadline_container}>
                  <div className={styles.deadline_title_container}>
                    <div className={styles.title}>Reminder</div>
                    <div>
                      <Button
                        variant="outlined"
                        className={styles.btn_add_deadline}
                        data-testid="overview-reminder-button"
                        onClick={() => {
                          setReminderData(caseReducer.case[data.id]);
                          setOpenReminderPopup(true);
                        }}
                      >
                        {data.reminder_name ? "Edit Reminder" : "Set Reminder"}
                      </Button>
                    </div>
                  </div>
                  <div className={styles.deadline_content}>
                    <div className={styles.deadline_info_box}>
                      <div className={styles.deadline_small_title_component}>
                        Title
                      </div>
                      <div
                        className={styles.deadline_big_title}
                        title={data.reminder_name}
                      >
                        {data.reminder_name
                          ? shortnString(data.reminder_name, 17)
                          : "----"}
                      </div>
                    </div>

                    <div className={styles.deadline_info_box}>
                      <div className={styles.deadline_small_title_component}>
                        Date
                      </div>
                      <div className={styles.deadline_big_title}>
                        {data.reminder_date
                          ? formatDatetime(data.reminder_date, "short")
                          : "----"}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {data.state === "STANDBY" && (
        <>
          <ActivateCasePopup
            open={openActivateCasePopup}
            close={() => setOpenActivateCasePopup(false)}
            data={data}
            caseId={data.id}
            stripeConnected={isConnected}
          />
          <StripeInfoPopup
            open={openStripeInfoPopup}
            close={() => setOpenStripeInfoPopup(false)}
            stripeUrl={stripeUrl}
          />
        </>
      )}
      <InfoPopup
        open={openInfoPopup}
        close={() => setOpenInfoPopup(false)}
        title={InfoPopupTitle}
      >
        <p>{infoPopupText}</p>
      </InfoPopup>
      <CancelCasePopup
        open={openCancelCasePopup}
        close={() => setOpenCancelCasePopup(false)}
        onValidLicenseSelected={openEvidancePopup}
        data={data}
        caseId={data.id}
        overviewData={data.overview}
      />
      <TemplatePopup
        open={templatePopupOpen}
        close={() => setTemplatePopupOpen(false)}
        data={data}
        caseId={data.id}
        overviewData={data.overview}
      />
      <AreYouSurePopup
        open={openResetCasePopup}
        close={() => setOpenResetCasePopup(false)}
        onClick={handleResetCase}
      >
        Are you sure you want to {data.was_activated ? "reactivate" : "reset"}{" "}
        this case?
      </AreYouSurePopup>
      <ClaimPopup
        open={openClaimPopup}
        close={() => setOpenClaimPopup(false)}
        data={data}
        caseId={data.id}
      />
      <ContactInfoPopup
        open={openContactInfoPopup}
        close={() => setOpenContactInfoPopup(false)}
        data={data}
        opponentData={data.opponent_contact_details}
        caseId={data.id}
      />
      <FindingsPopup
        open={openFindingsPopup}
        close={() => setOpenFindingsPopup(false)}
        data={data}
        overviewData={data.overview}
        imprintData={data.website_contact_details}
        findingsData={data.findings}
        caseId={data.id}
      />
      <AddFindingPopup
        open={openAddFindingPopup}
        close={() => setOpenAddFindingPopup(false)}
        caseId={data.id}
        allData={data}
      />
      <SelectEmailFlowPopup
        open={openEmailFlowPopup}
        close={() => setOpenEmailFlowPopup(false)}
        data={data}
        caseId={data.id}
        defaultFlow={flow}
      />
      <SelectTranslationPopup
        open={openTranslationPopup}
        close={() => setOpenTranslationPopup(false)}
        data={data}
        caseId={data.id}
      />
      {openReminderPopup && (
        <ReminderPopup
          open={openReminderPopup}
          close={() => setOpenReminderPopup(false)}
          data={reminderData}
          caseId={data.id}
          doClose={() => setOpenReminderPopup(false)}
        />
      )}
      <MailPopup
        title={`${
          emailData.name === emailConstants.NEW_MAIL
            ? "New mail"
            : "Mail preview: " + emailData.name
        }`}
        open={openMailPopup}
        close={() => setOpenMailPopup(false)}
        data={emailData}
        caseId={data.id}
        emailSetup={emailSetup}
      />
      <AddCaseTagPopup
        open={openAddCaseTagPopup}
        close={() => setOpenAddCaseTagPopup(false)}
        tags={data.tags}
        caseData={data}
      />
      <AppSnackbar {...snackbarProps} />
    </div>
  );
}

export default OverviewSection;
